"use client";

import * as React from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type SizeRow = {
  us: string;
  uk: string;
  eu: string;
  cm: string;
  fit: string;
  matched: boolean;
};
const brands = [{
  id: "wireguard",
  label: "WireGuard"
}, {
  id: "openvpn",
  label: "OpenVPN"
}, {
  id: "ikev2",
  label: "IKEv2"
}, {
  id: "sstp",
  label: "SSTP"
}];
const wireguardRows: SizeRow[] = [{
  us: "Fast",
  uk: "High",
  eu: "Modern",
  cm: "Secure",
  fit: "Best for speed",
  matched: true
}, {
  us: "Low",
  uk: "Low",
  eu: "Simple",
  cm: "Stable",
  fit: "Minimal overhead",
  matched: false
}, {
  us: "UDP",
  uk: "AES",
  eu: "ChaCha",
  cm: "Fast",
  fit: "Top performance",
  matched: false
}, {
  us: "New",
  uk: "High",
  eu: "Lean",
  cm: "Robust",
  fit: "Future-proof",
  matched: false
}, {
  us: "Yes",
  uk: "Yes",
  eu: "Yes",
  cm: "Yes",
  fit: "Highly efficient",
  matched: false
}];
const openvpnRows: SizeRow[] = [{
  us: "Slow",
  uk: "Med",
  eu: "Legacy",
  cm: "Secure",
  fit: "Highly reliable",
  matched: false
}, {
  us: "High",
  uk: "High",
  eu: "Heavy",
  cm: "Stable",
  fit: "Industry standard",
  matched: true
}, {
  us: "TCP",
  uk: "AES",
  eu: "RSA",
  cm: "Slow",
  fit: "Great compatibility",
  matched: false
}, {
  us: "Old",
  uk: "Med",
  eu: "Large",
  cm: "Robust",
  fit: "Proven security",
  matched: false
}, {
  us: "Yes",
  uk: "Yes",
  eu: "Yes",
  cm: "Yes",
  fit: "Versatile usage",
  matched: false
}];
const ikev2Rows: SizeRow[] = [{
  us: "Fast",
  uk: "High",
  eu: "Mobile",
  cm: "Stable",
  fit: "Best for mobile",
  matched: true
}, {
  us: "Low",
  uk: "Med",
  eu: "Quick",
  cm: "Fast",
  fit: "Auto-reconnect",
  matched: false
}, {
  us: "UDP",
  uk: "AES",
  eu: "IPSec",
  cm: "Fast",
  fit: "Strong encryption",
  matched: false
}, {
  us: "Mid",
  uk: "High",
  eu: "Lean",
  cm: "Robust",
  fit: "Low latency",
  matched: false
}, {
  us: "Yes",
  uk: "Yes",
  eu: "Yes",
  cm: "Yes",
  fit: "Great stability",
  matched: false
}];
const sstpRows: SizeRow[] = [{
  us: "Slow",
  uk: "Med",
  eu: "Win",
  cm: "Secure",
  fit: "Windows native",
  matched: false
}, {
  us: "High",
  uk: "High",
  eu: "Heavy",
  cm: "Stable",
  fit: "Bypasses firewalls",
  matched: true
}, {
  us: "TCP",
  uk: "AES",
  eu: "SSL",
  cm: "Slow",
  fit: "HTTPS traffic",
  matched: false
}, {
  us: "Old",
  uk: "Med",
  eu: "Large",
  cm: "Robust",
  fit: "Limited support",
  matched: false
}, {
  us: "Yes",
  uk: "Yes",
  eu: "Yes",
  cm: "Yes",
  fit: "Easy setup",
  matched: false
}];
const brandData: Record<string, SizeRow[]> = {
  wireguard: wireguardRows,
  openvpn: openvpnRows,
  ikev2: ikev2Rows,
  sstp: sstpRows
};
function SizeTable({
  rows
}: {
  rows: SizeRow[];
}) {
  return <TooltipProvider delayDuration={150}>
      <Table className="border-collapse">
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            <TableHead className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground sticky left-0 bg-background">
              Speed
            </TableHead>
            <TableHead className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Sec
            </TableHead>
            <TableHead className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Tech
            </TableHead>
            <TableHead className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Perf
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => <Tooltip key={i}>
              <TooltipTrigger asChild>
                <TableRow data-index={i} className="border-b border-dashed border-border odd:bg-muted/40 transition-colors duration-200 hover:bg-accent/40 cursor-default">
                  <TableCell className={row.matched ? "sticky left-0 bg-primary text-primary-foreground font-mono font-semibold text-base" : "sticky left-0 bg-background font-mono text-base text-foreground"}>
                    {row.us}
                  </TableCell>
                  <TableCell className="font-mono text-base text-foreground">
                    {row.uk}
                  </TableCell>
                  <TableCell className="font-mono text-base text-foreground">
                    {row.eu}
                  </TableCell>
                  <TableCell className="font-mono text-base text-foreground">
                    {row.cm}
                  </TableCell>
                </TableRow>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground font-mono text-[0.7rem] uppercase tracking-[0.12em]">
                {row.fit}
              </TooltipContent>
            </Tooltip>)}
        </TableBody>
      </Table>
    </TooltipProvider>;
}
export default function ProtocolComparison() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1935" id="protocolcomparison" className="bg-background text-foreground py-24 md:py-36 px-5 md:px-10">
      <div className="mx-auto max-w-[1320px]">
        <div ref={ref} className={"relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end transition-all duration-700 " + (isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-0 select-none font-serif font-bold text-foreground/[0.06] leading-none text-[8rem] md:text-[12rem]">
            02
          </span>

          <div className="lg:col-span-8 relative z-10">
            <p className="font-mono text-xs md:text-sm uppercase tracking-[0.22em] text-muted-foreground mb-6">
              § 02 / VPN PROTOCOLS
            </p>
            <h2 className="font-serif font-semibold leading-[0.98] tracking-[-0.02em] text-[clamp(2rem,4.5vw,3.75rem)] text-foreground">
              Speed &middot; Security &middot; Tech,{" "}
              <span className="relative inline-block">
                Side
                <span className="absolute -bottom-1 left-0 h-[3px] w-full bg-primary" />
                <span className="absolute -bottom-1 right-[-6px] h-[7px] w-[7px] rounded-sm bg-primary" />
              </span>{" "}
              By Side
            </h2>
            <p className="mt-6 max-w-xl font-default text-base md:text-lg leading-relaxed text-muted-foreground">
              Compare our supported VPN protocols. Hover any row
              for a technical insight from our security team.
            </p>
          </div>

          <div className="lg:col-span-4 relative z-10">
            <Tabs defaultValue="wireguard" className="w-full">
              <TabsList className="flex w-full flex-wrap h-auto gap-1 bg-muted p-1 rounded-lg">
                {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="flex-1 font-mono text-xs uppercase tracking-[0.1em] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md">
                    {brand.label}
                  </TabsTrigger>)}
              </TabsList>

              {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="mt-10 animate-in fade-in-0 duration-300">
                  <div className="block md:hidden">
                    <div className="rounded-md border border-dashed border-border p-1">
                      <ScrollArea className="w-full whitespace-nowrap">
                        <div className="min-w-[480px] p-3">
                          <SizeTable rows={brandData[brand.id]} />
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </div>
                  </div>
                </TabsContent>)}
            </Tabs>
          </div>
        </div>

        <div className="my-10 hidden md:block border-t border-dashed border-border" />

        <div className="hidden md:block">
          <Tabs defaultValue="wireguard" className="w-full">
            <TabsList className="mb-8 inline-flex h-auto gap-1 bg-muted p-1 rounded-lg">
              {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="font-mono text-xs uppercase tracking-[0.12em] px-5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md">
                  {brand.label}
                </TabsTrigger>)}
            </TabsList>

            {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="animate-in fade-in-0 duration-300">
                <div className="rounded-md border border-dashed border-border p-4 md:p-6 bg-card/20">
                  <SizeTable rows={brandData[brand.id]} />
                </div>
                <p className="mt-4 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted-foreground">
                  Primary row indicates the recommended protocol for this
                  use case
                </p>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}